/**
 * BrightPath Family Dashboard Hooks
 * 
 * TanStack Query hooks for dashboard-specific data
 */

import { useQuery } from '@tanstack/react-query';
import { useFamilyStore } from '@/features/family/stores/familyStore';
import apiClient from '@/core/lib/api-client';
import type {
  DashboardStats,
  ActivityItem,
  UpcomingAppointment,
  FamilyDashboardResponse,
} from '@/features/family/types/familyTypes';

// =============================================================================
// API Functions
// =============================================================================

const dashboardApi = {
  getStats: async (familyId: string): Promise<DashboardStats> => {
    const response = await apiClient.get<DashboardStats>(`/families/${familyId}/dashboard/stats`);
    return response.data;
  },

  getActivity: async (
    familyId: string,
    params?: { limit?: number }
  ): Promise<ActivityItem[]> => {
    const response = await apiClient.get<ActivityItem[]>(`/families/${familyId}/activity`, {
      params: params?.limit ? { limit: params.limit } : undefined,
    });
    return response.data;
  },

  getUpcomingAppointments: async (
    familyId: string,
    params?: { limit?: number }
  ): Promise<UpcomingAppointment[]> => {
    const response = await apiClient.get<UpcomingAppointment[]>(`/families/${familyId}/appointments/upcoming`, {
      params: params?.limit ? { limit: params.limit } : undefined,
    });
    return response.data;
  },

  getDashboard: async (familyId: string): Promise<FamilyDashboardResponse> => {
    const response = await apiClient.get<FamilyDashboardResponse>(`/families/${familyId}/dashboard`);
    return response.data;
  },
};

// =============================================================================
// Query Keys
// =============================================================================

export const dashboardKeys = {
  all: ['dashboard'] as const,
  family: (familyId: string) => [...dashboardKeys.all, familyId] as const,
  stats: (familyId: string) => [...dashboardKeys.family(familyId), 'stats'] as const,
  activity: (familyId: string, params?: { limit?: number }) =>
    [...dashboardKeys.family(familyId), 'activity', params] as const,
  appointments: (familyId: string, params?: { limit?: number }) =>
    [...dashboardKeys.family(familyId), 'appointments', params] as const,
  full: (familyId: string) => [...dashboardKeys.family(familyId), 'full'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch dashboard statistics
 */
export function useDashboardStats(familyId?: string | null) {
  const currentFamilyId = useFamilyStore((state) => state.currentFamilyId);
  const resolvedFamilyId = familyId ?? currentFamilyId;

  return useQuery({
    queryKey: dashboardKeys.stats(resolvedFamilyId!),
    queryFn: () => dashboardApi.getStats(resolvedFamilyId!),
    enabled: !!resolvedFamilyId,
    staleTime: 2 * 60 * 1000, // 2 minutes - stats update more frequently
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Fetch recent activity feed
 */
export function useRecentActivity(
  familyId?: string | null,
  options?: { limit?: number }
) {
  const currentFamilyId = useFamilyStore((state) => state.currentFamilyId);
  const resolvedFamilyId = familyId ?? currentFamilyId;
  const limit = options?.limit ?? 10;

  return useQuery({
    queryKey: dashboardKeys.activity(resolvedFamilyId!, { limit }),
    queryFn: () => dashboardApi.getActivity(resolvedFamilyId!, { limit }),
    enabled: !!resolvedFamilyId,
    staleTime: 1 * 60 * 1000, // 1 minute - activity updates frequently
  });
}

/**
 * Fetch upcoming appointments
 */
export function useUpcomingAppointments(
  familyId?: string | null,
  options?: { limit?: number }
) {
  const currentFamilyId = useFamilyStore((state) => state.currentFamilyId);
  const resolvedFamilyId = familyId ?? currentFamilyId;
  const limit = options?.limit ?? 5;

  return useQuery({
    queryKey: dashboardKeys.appointments(resolvedFamilyId!, { limit }),
    queryFn: () => dashboardApi.getUpcomingAppointments(resolvedFamilyId!, { limit }),
    enabled: !!resolvedFamilyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch complete dashboard data in one request
 * Use this for initial load to reduce API calls
 */
export function useDashboard(familyId?: string | null) {
  const currentFamilyId = useFamilyStore((state) => state.currentFamilyId);
  const resolvedFamilyId = familyId ?? currentFamilyId;

  return useQuery({
    queryKey: dashboardKeys.full(resolvedFamilyId!),
    queryFn: () => dashboardApi.getDashboard(resolvedFamilyId!),
    enabled: !!resolvedFamilyId,
    staleTime: 2 * 60 * 1000,
  });
}

// =============================================================================
// Helper Hooks
// =============================================================================

/**
 * Get all dashboard data using the combined endpoint
 * Falls back to individual queries if needed
 */
export function useDashboardData(familyId?: string | null) {
  const dashboardQuery = useDashboard(familyId);

  return {
    stats: dashboardQuery.data?.stats,
    activity: dashboardQuery.data?.recentActivity ?? [],
    appointments: dashboardQuery.data?.upcomingAppointments ?? [],
    children: dashboardQuery.data?.children ?? [],
    isLoading: dashboardQuery.isLoading,
    isError: dashboardQuery.isError,
    error: dashboardQuery.error,
    refetch: dashboardQuery.refetch,
  };
}

/**
 * Prefetch dashboard data (useful for navigation)
 */
export function usePrefetchDashboard() {
  // Implementation would use queryClient.prefetchQuery
  // Left as stub for now
  return {
    prefetch: (_familyId: string) => {
      // queryClient.prefetchQuery(...)
    },
  };
}

// =============================================================================
// Mock Data (for development/demo)
// =============================================================================

export const mockDashboardStats: DashboardStats = {
  therapySessionsThisMonth: 12,
  therapySessionsChange: 2,
  seizureFreeDays: 28,
  seizureFreeDaysNote: 'Personal best! 🎉',
  iepGoalsProgress: 67,
  iepGoalsNote: 'On track for review',
  ytdExpenses: 4230,
  ytdExpensesNote: 'Tax-ready export available',
};

export const mockRecentActivity: ActivityItem[] = [
  {
    id: '1',
    type: 'health_log',
    title: 'PT Session Completed',
    description: 'Hip strengthening exercises - good progress',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    childName: 'Emma',
  },
  {
    id: '2',
    type: 'medication',
    title: 'Medication Logged',
    description: 'Morning doses confirmed',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    type: 'document_uploaded',
    title: 'Document Uploaded',
    description: 'MRI results from Dr. Chen',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

export const mockUpcomingAppointments: UpcomingAppointment[] = [
  {
    id: '1',
    title: 'Neurology Follow-up',
    providerName: 'Dr. Martinez',
    locationType: 'in_person',
    startsAt: '2025-01-15T10:30:00Z',
    endsAt: '2025-01-15T11:00:00Z',
    status: 'confirmed',
    childName: 'Emma',
  },
  {
    id: '2',
    title: 'OT Session',
    providerName: 'Bright Futures Therapy',
    locationType: 'in_person',
    startsAt: '2025-01-17T14:00:00Z',
    endsAt: '2025-01-17T15:00:00Z',
    status: 'scheduled',
  },
  {
    id: '3',
    title: 'IEP Review Meeting',
    locationName: 'Lincoln Elementary',
    locationType: 'in_person',
    startsAt: '2025-01-20T15:30:00Z',
    endsAt: '2025-01-20T16:30:00Z',
    status: 'scheduled',
  },
];

